export interface DSLDResult {
    dsldId: string;
    name: string;
    brandName: string;
}

export interface DSLDLabel {
    fullName: string;
    dosage: string | null;
    frequency: string | null;
    adminNotes: string | null;
    route: string | null;
}

const API_BASE = "https://api.ods.od.nih.gov/dsld/v9";
const DSLD_API_KEY = process.env.EXPO_PUBLIC_DSLD_API_KEY ?? "";

export async function searchSupplements(query: string, maxList = 15): Promise<DSLDResult[]> {
    if (!query || query.length < 2) {
        return [];
    }

    try {
        const url = `${API_BASE}/browse-products/?method=by_keyword&q=${encodeURIComponent(query)}&api_key=${DSLD_API_KEY}`;
        const res = await fetch(url);
        if (!res.ok) {
            return [];
        }

        const data = await res.json();
        const hits = data?.hits ?? [];

        if (!Array.isArray(hits) || hits.length === 0) {
            return [];
        }

        // Deduplicate by fullName (many labels for the same product)
        const seen = new Set<string>();
        const results: DSLDResult[] = [];

        for (const hit of hits) {
            const src = hit._source;
            if (!src?.fullName) {
                continue;
            }
            const key = src.fullName.toLowerCase();
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            results.push({
                dsldId: String(hit._id),
                name: src.fullName,
                brandName: src.brandName ?? ""
            });
            if (results.length >= maxList) {
                break;
            }
        }

        return results;
    } catch {
        return [];
    }
}

/**
 * Fetch full label data for a DSLD product.
 * Maps to visit_medications table fields only.
 */
export async function getSupplementLabel(dsldId: string): Promise<DSLDLabel | null> {
    try {
        const url = `${API_BASE}/label/${dsldId}?api_key=${DSLD_API_KEY}`;
        const res = await fetch(url);
        if (!res.ok) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data: any = await res.json();

        // Ingredient quantities can be per serving and multi-ingredient labels
        // have no single strength. Collect the product strength from the nurse;
        // never silently use the first ingredient as the whole product's strength.
        const dosage: string | null = null;

        // Extract frequency/directions from statements
        let frequency: string | null = null;
        let adminNotes: string | null = null;
        const statements = data.statements ?? [];
        for (const stmt of statements) {
            const type = stmt.type ?? "";
            const notes = stmt.notes ?? "";
            if (
                type.includes("Suggested") ||
                type.includes("Recommended") ||
                type.includes("Directions")
            ) {
                // Try to extract a simple frequency
                if (
                    notes.toLowerCase().includes("once daily") ||
                    notes.toLowerCase().includes("one time daily")
                ) {
                    frequency = "Once daily";
                } else if (
                    notes.toLowerCase().includes("twice daily") ||
                    notes.toLowerCase().includes("two times daily")
                ) {
                    frequency = "Twice daily";
                } else if (notes.toLowerCase().includes("three times daily")) {
                    frequency = "Three times daily";
                } else if (
                    notes.toLowerCase().includes("once a day") ||
                    notes.toLowerCase().includes("one a day")
                ) {
                    frequency = "Once daily";
                } else if (
                    notes.toLowerCase().includes("twice a day") ||
                    notes.toLowerCase().includes("two a day")
                ) {
                    frequency = "Twice daily";
                } else if (notes.match(/(\d+)\s*times?\s*(per|a)\s*day/i)) {
                    const match = notes.match(/(\d+)\s*times?\s*(per|a)\s*day/i);
                    if (match) {
                        const n = parseInt(match[1]);
                        if (n === 1) {
                            frequency = "Once daily";
                        } else if (n === 2) {
                            frequency = "Twice daily";
                        } else if (n === 3) {
                            frequency = "Three times daily";
                        } else {
                            frequency = `${n} times daily`;
                        }
                    }
                }
                adminNotes = notes;
                break;
            }
        }

        // Extract route from physical state
        let route: string | null = null;
        const form = data.physicalState?.langualCodeDescription ?? "";
        const formLower = form.toLowerCase();
        if (
            formLower.includes("tablet") ||
            formLower.includes("capsule") ||
            formLower.includes("softgel") ||
            formLower.includes("gummy") ||
            formLower.includes("lozenge") ||
            formLower.includes("powder") ||
            formLower.includes("liquid") ||
            formLower.includes("chewable")
        ) {
            route = "Oral";
        } else if (
            formLower.includes("topical") ||
            formLower.includes("cream") ||
            formLower.includes("ointment")
        ) {
            route = "Topical";
        }

        return {
            fullName: data.fullName ?? "",
            dosage,
            frequency,
            adminNotes,
            route
        };
    } catch {
        return null;
    }
}
